import { useState, useEffect } from 'react';
import { imageUrl } from '../lib/images';
import Lightbox from './Lightbox';

export default function TreatmentGallery({ treatmentSlug, title = "Billeder fra behandlingen", subtitle }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    if (!treatmentSlug) return;
    setLoading(true);

    fetch(`/_api/public/v2/content/gallerier?behandling_slug=${encodeURIComponent(treatmentSlug)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          setGallery(data.items[0]);
        } else {
          fetch('/_api/public/v2/content/gallerier')
            .then((r) => (r.ok ? r.json() : null))
            .then((allData) => {
              const matched = allData?.items?.find(
                (g) =>
                  g.behandling_slug === treatmentSlug ||
                  g.treatment_slug === treatmentSlug ||
                  g.slug === treatmentSlug ||
                  g.slug?._ === treatmentSlug
              );
              setGallery(matched || null);
            });
        }
      })
      .catch(() => setGallery(null))
      .finally(() => setLoading(false));
  }, [treatmentSlug]);

  if (loading) {
    return (
      <div className="py-6 flex items-center justify-center text-slate-400">
        <i className="fa-solid fa-circle-notch fa-spin mr-2" aria-hidden="true" />
        <span className="text-sm">Henter galleri...</span>
      </div>
    );
  }

  const images = gallery?.billeder || gallery?.images || [];
  if (images.length === 0) return null;

  const galleryTitle = gallery?.titel || gallery?.title || title;
  const galleryDesc = gallery?.beskrivelse || gallery?.description || subtitle;

  return (
    <div className="mt-8 pt-6 border-t border-[#E8E1D5]">
      {/* Header - NO paragraph above heading */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-4">
        <div>
          <h4 className="text-lg sm:text-xl font-bold text-[#1E3A5F] font-serif">
            {galleryTitle}
          </h4>
          {galleryDesc && (
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5">{galleryDesc}</p>
          )}
        </div>
        <div className="text-xs text-slate-400 font-medium hidden sm:block">
          Klik for at forstørre
        </div>
      </div>

      {/* Grid of gallery images - sharp edges */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {images.map((img, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => {
              setPhotoIndex(idx);
              setLightboxOpen(true);
            }}
            className="group relative aspect-4/3 overflow-hidden bg-[#E8E1D5] border border-[#E8E1D5] hover:shadow-md transition-shadow text-left cursor-pointer"
            aria-label={`Vis forstørret billede ${idx + 1}`}
          >
            <img
              src={imageUrl(img, 'width=600,height=450,fit=cover,quality=80,format=auto')}
              alt={`${galleryTitle} - billede ${idx + 1}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <span className="w-8 h-8 bg-white/95 text-[#1E3A5F] flex items-center justify-center shadow">
                <i className="fa-solid fa-magnifying-glass-plus text-xs" aria-hidden="true" />
              </span>
            </div>
          </button>
        ))}
      </div>

      <Lightbox
        images={images}
        currentIndex={photoIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onSelectIndex={(newIdx) => setPhotoIndex(newIdx)}
      />
    </div>
  );
}
